/* eslint-disable id-blacklist */
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { IDynamicDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { getExportSettingsControl, getImportSettingsControls, snowflakeTableMetadata } from './snowflaketable-metadata';
import { preCopyScriptMetadata } from '../consts/metadata-consts';
import { getConnectorV2Name, UpdateMethod } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { getAppendOption, getSCD2Option, getUpsertOption } from 'app/registries/consts/copy-job-const';
import { binaryPhysicalTypes, ColumnPhysicalType, ColumnType } from 'app/shared/utils/column-type.util';
import { snowflakeVersioning } from 'app/shared/constants/variable-constants';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';


export const snowflakeV2TableMetadata: IDynamicDatasetTypeMetadata = async () => {
    const supportAutoCreate = __IS_FABRIC_DPE__;
    const supportUpsert = __IS_FABRIC_DPE__;
    return {
        ...snowflakeTableMetadata,
        notSupportTypeConversion: false, // override v1, v2 supports type conversion
        useExternalCommandWhenSource: true,
        useExternalCommandWhenSink: true,
        type: __IS_FABRIC_DPE__ ? adfModelTypeMap.snowflake.datasetType : adfModelTypeMap.snowflakeV2.datasetType,
        displayName: getConnectorV2Name(stringResources.Snowflake_DisplayText, __IS_FABRIC_DPE__),
        datasetNamePrefix: adfModelTypeMap.snowflake.datasetType,
        supportLinkedServiceTypes: [
            __IS_FABRIC_DPE__ ? adfModelTypeMap.snowflake.linkedServiceType : adfModelTypeMap.snowflakeV2.linkedServiceType
        ],
        supportAutoTableCreation: supportAutoCreate ? `{ version === '1.1' }` : false,
        supportEditPhysicalType: supportAutoCreate ? `{ version === '1.1' }` : false,
        disabled: false,
        warningMessage: undefined,
        warningMessageHyperlink: undefined,
        copySourceControls: [getExportSettingsControl(true)],
        notSupportAdditionalColumns: !__IS_FABRIC_DPE__,
        copySinkControls: [
            {
                type: 'radio-switch',
                label: stringResources.ModelName_WriteBehavior,
                hide: `{ !(${supportUpsert} && owner && owner.sinkDataset && owner.sinkDataset.version === "1.1") }`,
                bind: 'writeBehavior',
                isParameterizable: true,
                showDynamically: true,
                outsideAdvancedSectionInFabric: true,
                options: [
                    {
                        value: undefined,
                        label: stringResources.WriteBehavior_Option_Insert
                    },
                    {
                        value: writeBehaviorOptionValues.upsert,
                        label: stringResources.WriteBehavior_Option_Upsert
                    }
                ],
                defaultValue: undefined,
                cases: [
                    {
                        caseValue: writeBehaviorOptionValues.upsert,
                        children: [
                            {
                                type: 'sql-upsert',
                                bind: {
                                    keys: 'writeBehaviorKeys'
                                },
                                hasTempDB: false,
                                hasInterimSchemaName: false,
                                keyColumnsAttributeName: 'writeBehaviorKeys',
                                parameterizable: true
                            }
                        ]
                    }
                ]
            },
            preCopyScriptMetadata(),
            getImportSettingsControls(true)
        ],
        lookupSourceControls: [
            {
                type: 'parameterizable',
                child: {
                    type: 'checkbox',
                    bind: 'treatDecimalAsString',
                    label: stringResources.TreatDecimalAsString,
                    defaultValue: true,
                    useDefaultValueWhenValueIsNull: true,
                    labelPosition: 'left'
                },
                hide: __IS_FABRIC_DPE__ ?
                    `{ owner && owner.sourceDataset && owner.sourceDataset.version !== '1.1' }` :
                    `{ owner.sourceDataset && owner.sourceDataset.linkedService && owner.sourceDataset.linkedService.version !== '1.1' }`,
                dynamicContentTextClass: 'input-spaced'
            },
            getExportSettingsControl()
        ],
        supportSameTypePhysicalCopy: supportAutoCreate,
        supportedPhysicalTypes: supportAutoCreate ? [
            ColumnPhysicalType.Number,
            ColumnPhysicalType.Float,
            ColumnPhysicalType.Varchar,
            ColumnPhysicalType.Binary,
            ColumnPhysicalType.Boolean,
            ColumnPhysicalType.Date,
            ColumnPhysicalType.Time,
            ColumnPhysicalType.Timestamp_ntz,
            ColumnPhysicalType.Timestamp_tz,
            ColumnPhysicalType.Timestamp_ltz,
        ] : undefined,
        LogicalToPhysicalMapping: supportAutoCreate ? {
            decimal: ColumnPhysicalType.Number,
            byte: ColumnPhysicalType.Number,
            int16: ColumnPhysicalType.Number,
            int32: ColumnPhysicalType.Number,
            int64: ColumnPhysicalType.Number,
            sbyte: ColumnPhysicalType.Number,
            uint16: ColumnPhysicalType.Number,
            uint32: ColumnPhysicalType.Number,
            uint64: ColumnPhysicalType.Number,
            double: ColumnPhysicalType.Float,
            single: ColumnPhysicalType.Float,
            guid: ColumnPhysicalType.Varchar,
            string: ColumnPhysicalType.Varchar,
            'byte[]': ColumnPhysicalType.Binary,
            boolean: ColumnPhysicalType.Boolean,
            date: ColumnPhysicalType.Date,
            time: ColumnPhysicalType.Time,
            datetime: ColumnPhysicalType.Timestamp_ntz,
            datetimeoffset: ColumnPhysicalType.Timestamp_tz
        } : undefined,
        PhysicalToLogicalMapping: supportAutoCreate ? {
            number: ColumnType.Decimal,
            float: ColumnType.Double,
            varchar: ColumnType.String,
            binary: ColumnType.ByteArray,
            date: ColumnType.Date,
            time: ColumnType.Time,
            timestamp_ntz: ColumnType.DateTime,
            timestamp_tz: ColumnType.DateTimeOffset,
            timestamp_ltz: ColumnType.DateTimeOffset
        } : undefined,
        binaryPhysicalTypes,
        binaryLengthMap: {
            varchar: {
                minValue: 1,
                maxValue: 16777216,
                defaultValue: 'MAX',
                supportMaxValue: true
            },
            binary: {
                minValue: 1,
                maxValue: 8388608,
                defaultValue: 'MAX',
                supportMaxValue: true
            }
        },
        precisionMap: {
            number: {
                minValue: 1,
                maxValue: 38,
                defaultValue: 38
            },
            timestamp_tz: {
                minValue: 0,
                maxValue: 9,
                defaultValue: 9
            },
            timestamp_ltz: {
                minValue: 0,
                maxValue: 9,
                defaultValue: 9
            }
        },
        scaleMap: {
            number: {
                minValue: 0,
                maxValue: 37,
                defaultValue: 0,
                smallerThanPrecision: true
            }
        },
        copyJob: {
            fullLoadBehavior: {
                supportFullLoadBehavior: true
            },
            multipleQuery: true,
            updateMethod: {
                supportUpdateMethod: true,
                options: GlobalVariables.copyJobSnowflakeSCD2
                    ? [getAppendOption(), getUpsertOption(), getSCD2Option()]
                    : [getAppendOption(), getUpsertOption()],
                updateMethodProperties: ['writeBehavior', 'upsertSettings'],
                defaultUpdateMethod: UpdateMethod.Append,
                upsertKeyColumnRequired: true
            }
        },
        versionConfig: supportAutoCreate || supportUpsert ? snowflakeVersioning : undefined
    };
}
